import {
  isTabsWrapper,
  isTabsMenu,
  isTabsContent,
  isTabsLinkElement,
  isTabsPane,
  isTabsPair,
} from '@packages/utilities/elementTypeUtils';
import {findChild} from '@packages/utilities/core-elements';
import {None, Some, type Option} from '@packages/utilities/fp/option';

import {
  EBoolean,
  EElement,
  EEnum,
  EList,
  ERecord,
  EText,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {concatTo, constant, emptyArray} from '@packages/utilities/fp/utils';
import {
  matchExpression,
  Expressions,
  Plugins,
  Component,
} from '@packages/systems/wfdl';
import {DATA_W_TAB_PATH} from '@packages/shared/render/plugins/Tabs/constants';
import {identity} from 'lodash';
import {increment} from '@packages/utilities/string-utils';
import {getSecond} from '@packages/utilities/Pair';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {qualify} from '@packages/systems/wfdl-Qualified';

export const getTabAttr = (element: ExpressionElement): string =>
  Expressions.getElementDataAt(element, DATA_W_TAB_PATH, 'Text', '');

export const tabsPair =
  (element: ExpressionElement) =>
  (tree: Expression): Option<[ExpressionElement, ExpressionElement]> => {
    const tabName = getTabAttr(element);
    const sameTabName = (child: ExpressionElement) =>
      getTabAttr(child) === tabName;
    const wrapper = Expressions.closestElement(tree, element.id, isTabsWrapper);
    if (!wrapper) return None;

    if (isTabsLinkElement(element)) {
      const tabsContent = findChild(wrapper, isTabsContent);
      if (tabsContent) {
        const pane = findChild(tabsContent, sameTabName);
        return pane
          ? Some<[ExpressionElement, ExpressionElement]>([element, pane])
          : None;
      }
    }

    if (isTabsPane(element)) {
      const tabsMenu = findChild(wrapper, isTabsMenu);
      if (tabsMenu) {
        const link = findChild(tabsMenu, sameTabName);
        return link
          ? Some<[ExpressionElement, ExpressionElement]>([link, element])
          : None;
      }
    }

    return None;
  };

export const getTabContentTexts = matchExpression({
  Element: (el: ExpressionElement) => {
    if (isTabsPane(el)) {
      const attr = Expressions.getIn(
        Expressions.getElementData(el),
        DATA_W_TAB_PATH
      );
      if (attr) {
        return Expressions.stop([Expressions.getText(attr) || '']);
      }
    }
    return Expressions.resume(emptyArray);
  },
  _: constant(Expressions.resume(emptyArray)),
});

export const generateUniqueTabNameFromWrapper = (
  wrapper: ExpressionElement,
  prefix: string
): string | null => {
  if (!isTabsWrapper(wrapper)) return null;
  const contentElement = findChild(wrapper, isTabsContent);
  if (!contentElement) return null;

  const content = Expressions.getElementData(contentElement);
  const names = Expressions.everything(concatTo)(getTabContentTexts)(content);

  return increment(names, prefix);
};

export const replaceTabPairData = (
  element: ExpressionElement,
  newName: string
): ExpressionElement => {
  if (!isTabsPair(element)) return element;

  const expr = Expressions.getElementData(element);
  if (!expr) return element;

  const replaceTabData = matchExpression<Expression>({
    Record: (record) =>
      'data-w-tab' in record
        ? ERecord({...record, 'data-w-tab': EText(newName)})
        : ERecord(record),
    _: identity,
  });

  const updatedExpr = Expressions.everywhere(replaceTabData)(expr);

  return Expressions.setElementData(element, updatedExpr);
};

export const generateNewTabsLinkExpression = (
  tabName: string,
  seedId: string
) => {
  return Expressions.getElement(
    getSecond(
      instantiateExpressionWithUniqueIds(
        EElement({
          id: 'Tabs Link',
          type: qualify(Plugins.name('Tabs'), Component.name('TabsLink')),
          data: ERecord({
            block: EText('inline'),
            tabs: ERecord({type: EText('link')}),
            attr: ERecord({'data-w-tab': EText(tabName)}),
            children: EList([
              EElement({
                id: 'Tabs Wrapper Block',
                type: qualify(Plugins.name('Basic'), Component.name('Block')),
                data: ERecord({
                  tag: EEnum('div'),
                  text: EBoolean(true),
                  children: EList([
                    EElement({
                      id: 'Tabs Wrapper String',
                      type: qualify(
                        Plugins.name('Basic'),
                        Component.name('String')
                      ),
                      data: EText(tabName),
                    }),
                  ]),
                }),
              }),
            ]),
          }),
        })
      )(seedId)
    )
  );
};

export const generateNewTabsPaneExpression = (
  tabName: string,
  seedId: string
) => {
  return Expressions.getElement(
    getSecond(
      instantiateExpressionWithUniqueIds(
        EElement({
          id: 'Tabs Pane',
          type: qualify(Plugins.name('Tabs'), Component.name('TabsPane')),
          data: ERecord({
            tag: EEnum('div'),
            tabs: ERecord({type: EText('pane')}),
            attr: ERecord({'data-w-tab': EText(tabName)}),
            children: EList([]),
          }),
        })
      )(seedId)
    )
  );
};
